import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Package, ExternalLink, Trash2, Pencil, Camera, Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '@/lib/api'
import { getProductScreenshotThumbnailUrl } from '@/lib/assets'
import { formatDate, truncate } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toaster'
import type { Product } from '@/types'

const PAGE_SIZE = 30

export function ProductsPage() {
  const queryClient = useQueryClient()
  const [offset, setOffset] = useState(0)

  const { data: products, isLoading } = useQuery({
    queryKey: ['products', offset],
    queryFn: () => api.getProducts(offset, PAGE_SIZE, { includeScreenshotUrls: false }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteProduct(id),
    onSuccess: () => {
      // If the last item on this page was deleted, go back one page
      if (products && products.items.length === 1 && offset > 0) {
        setOffset((prev) => Math.max(0, prev - PAGE_SIZE))
      }
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast({ title: 'Product deleted' })
    },
  })

  const screenshotMutation = useMutation({
    mutationFn: ({ productId, file }: { productId: string; file: File }) =>
      api.uploadProductScreenshot(productId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast({ title: 'Screenshot updated' })
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to upload screenshot',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      })
    },
  })

  return (
    <div className="space-y-6">
      {/* Action Bar */}
      <div className="flex justify-end">
        <Link to="/products/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </Button>
        </Link>
      </div>

      {/* Products Grid */}
      {isLoading ? (
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="mb-6 break-inside-avoid rounded-xl border border-gray-200/60 bg-white shadow-sm overflow-hidden"
            >
              {i % 3 !== 0 && <div className="h-40 bg-gray-200/80 animate-pulse" />}
              <div className="p-4 space-y-3">
                <div className="h-5 bg-gray-200/80 animate-pulse rounded w-2/3" />
                <div className="h-4 bg-gray-200/80 animate-pulse rounded w-full" />
                <div className="h-4 bg-gray-200/80 animate-pulse rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : products?.items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200/60 shadow-sm">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
            <Package className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-base font-medium text-gray-900">No products yet</h3>
          <p className="text-gray-600 mb-4">Create your first product to start generating creatives</p>
          <Link to="/products/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
            {products?.items.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onDelete={(id) => {
                  if (confirm('Delete this product?')) {
                    deleteMutation.mutate(id)
                  }
                }}
                onScreenshotUpload={(productId, file) =>
                  screenshotMutation.mutate({ productId, file })
                }
                isUploadingScreenshot={
                  screenshotMutation.isPending &&
                  screenshotMutation.variables?.productId === product.id
                }
              />
            ))}
          </div>

          {/* Pagination */}
          {products && (
            <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200/60 shadow-sm px-4 py-3">
              <p className="text-sm text-gray-600">
                Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, products.total)} of {products.total} products
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset + PAGE_SIZE >= products.total}
                  onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function ProductCard({
  product,
  onDelete,
  onScreenshotUpload,
  isUploadingScreenshot,
}: {
  product: Product
  onDelete: (id: string) => void
  onScreenshotUpload: (productId: string, file: File) => void
  isUploadingScreenshot: boolean
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const screenshotThumbnailUrl = getProductScreenshotThumbnailUrl(product)
  const hasScreenshot = Boolean(screenshotThumbnailUrl)

  return (
    <div className="group mb-6 break-inside-avoid overflow-hidden rounded-xl border border-gray-200/60 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onScreenshotUpload(product.id, file)
          e.target.value = ''
        }}
      />

      {hasScreenshot && (
        <div className="relative h-40 bg-gray-100">
          <img
            src={screenshotThumbnailUrl!}
            alt={product.product_name}
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <button
            type="button"
            className="absolute inset-0 flex cursor-pointer items-center justify-center bg-black/0 transition-colors group-hover:bg-black/40"
            onClick={(e) => {
              e.preventDefault()
              fileInputRef.current?.click()
            }}
          >
            <div className="flex flex-col items-center gap-1 text-white opacity-0 transition-opacity group-hover:opacity-100">
              {isUploadingScreenshot ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <>
                  <Camera className="h-6 w-6" />
                  <span className="text-xs font-medium">Change</span>
                </>
              )}
            </div>
          </button>
        </div>
      )}

      {/* Product info */}
      <div className="p-4">
        <Link
          to={`/products/${product.id}`}
          className="text-lg font-semibold text-gray-900 hover:text-primary"
        >
          {truncate(product.product_name, 40)}
        </Link>

        {product.description && (
          <p className="text-sm text-gray-600 mt-1">
            {truncate(product.description, 80)}
          </p>
        )}

        <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
          <span>{product.total_assets} assets</span>
          <span>Created {formatDate(product.created_at)}</span>
        </div>

        {product.source_url && (
          <a
            href={product.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2"
          >
            <ExternalLink className="h-3 w-3" />
            View source
          </a>
        )}

        <div className="flex justify-end gap-1 mt-4 pt-4 border-t">
          <Link to={`/products/${product.id}`}>
            <Button variant="ghost" size="sm">
              <Pencil className="h-4 w-4" />
            </Button>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => onDelete(product.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
